import { useEffect } from "react";
import { getVipList } from "@/api/vip";
import { setGlobalValue } from "@/stores/GlobalState";
import { useUserStore } from "@/modules/user/stores";
import { PROXY_ACTIVATED_EVENT } from "@/modules/domainGuard";

/**
 * 拉取 VIP 红点数量写入全局。首屏与域名容灾软恢复共用，
 * 避免两处各写一份取值逻辑而后续只改到其中一处。
 * 调用方负责判断登录态（见下方 hook 的注释）。
 */
const refreshVipBadgeCount = async () => {
  try {
    const response = await getVipList();
    if (response?.data?.data.canClaimCount !== undefined) {
      setGlobalValue("vipBadgeCount", response.data.data.canClaimCount);
    }
  } catch (error) {
    console.error("Failed to fetch VIP badge count:", error);
  }
};

/**
 * 底部导航的 VIP 红点计数。
 *
 * <p>抽成共享 hook 而不是写在各 variants/template* 里：底栏有多套模板，
 * 逻辑逐字相同，分散写会出现「只改到其中一个模板」的漏改。
 */
export function useVipBadgeCount(): void {
  const isAuthenticated = useUserStore(state => state.isAuthenticated());

  // 首屏取值；isAuthenticated 变化（登录/退出）时重新拉取。
  // 未登录直接跳过并清零：vip_center 需登录，匿名调用后端返回 500 REQUIRE LOGIN，
  // 白付一次回源 + 一条后端 500，而 badge 对未登录也无意义。
  useEffect(() => {
    if (!isAuthenticated) {
      setGlobalValue("vipBadgeCount", 0);
      return;
    }
    const timer = setTimeout(() => {
      void refreshVipBadgeCount();
    }, 500);
    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  // 域名容灾软恢复：底栏在 App 的延迟挂载闸门之外，红点属于首波请求
  // （挂载后 500ms），主域名被封时这一发会失败，而上面的 effect 依赖
  // isAuthenticated 不变就不再重试，红点整场不显示。代理切到备用域名后补一次。
  useEffect(() => {
    if (!isAuthenticated) return;
    const onProxyActivated = () => {
      void refreshVipBadgeCount();
    };
    window.addEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    return () => {
      window.removeEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    };
  }, [isAuthenticated]);
}
